/**
 * Seamless video playlist playback via Media Source Extensions.
 *
 * This is the architecturally-correct fix for the video → video transition
 * freeze. Every prior attempt (src swap, dual-element prime, canvas mask)
 * had the same problem: changing <video>.src forces the H.264 decoder to
 * tear down and reinitialize, and there's a hardware-bound gap (anywhere
 * from ~100 ms on real LG hardware to several seconds on the simulator's
 * software decoder) during which the surface shows the previous frame.
 *
 * MSE eliminates the gap entirely: one persistent <video> + one persistent
 * MediaSource + one persistent SourceBuffer. We append the bytes of the
 * next item to the SAME SourceBuffer as the current one, with a
 * timestampOffset bookkeeping bump. The decoder sees one continuous
 * timeline and never resets. The seam between items is just a content
 * cut — visually equivalent to a hard cut in a single video file.
 *
 * Required: the asset MUST be a fragmented MP4 (movflags
 * +frag_keyframe+empty_moov+default_base_moof, set in the worker since
 * the codec-normalization move). For legacy non-fragmented assets, the
 * caller falls back to the canvas-mask path in renderer.ts.
 *
 * Memory budget on TV-class Chromium is ~30 MB per SourceBuffer. We
 * aggressively remove buffered ranges before the playhead with sb.remove()
 * on every timeupdate so we don't trip QuotaExceededError mid-playlist.
 */

import { getCachedAsset } from '../utils/storage';
import { manifestLoader } from './manifest-loader';
import type { ManifestAsset } from '../types';

// Heuristic: this much of past playback can sit in the SourceBuffer before
// we trim it. Two seconds is enough to absorb a stutter or pause, and
// generously below the 30 MB quota even at 4K 12 Mbps.
const KEEP_PAST_SEC = 2;

// MP4 box sniff: read this many bytes from the start to look for `styp`,
// `moof`, or `moov` boxes. A fragmented MP4 starts with `styp` (segment
// type) or has `moof` boxes interleaved with `mdat`; a non-fragmented MP4
// has its `moov` upfront with no `moof` boxes.
const SNIFF_BYTES = 64 * 1024;

interface TimelineEntry {
  assetId: string;
  startSec: number;
  durationSec: number;
}

export class MseVideoPlayer {
  private video: HTMLVideoElement | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private mediaSourceObjectUrl: string | null = null;

  private timeline: TimelineEntry[] = [];
  private nextTimestampOffsetSec = 0;
  private appendedItemIds = new Set<string>();

  private sourceReady: Promise<void> | null = null;
  private resolveSourceReady: (() => void) | null = null;

  // Codec string we register with the SourceBuffer. Matches the
  // FLEET_PROFILE in worker-service/video-processor.ts: avc1.640028 is
  // High@L4.0, mp4a.40.2 is AAC-LC. If the worker ever changes profile,
  // this must change too.
  private static readonly FLEET_CODEC = 'video/mp4; codecs="avc1.640028, mp4a.40.2"';

  /**
   * Feature detection. Returns false for browsers without MSE (very old
   * webOS firmware, some embedded WebViews). Callers should fall back to
   * the canvas-mask single-element path when this returns false.
   */
  static isSupported(): boolean {
    return (
      typeof MediaSource !== 'undefined' &&
      MediaSource.isTypeSupported(MseVideoPlayer.FLEET_CODEC)
    );
  }

  /**
   * Build the persistent <video> + MediaSource and append to the container.
   * Idempotent — calling attach() twice is a no-op.
   */
  attach(container: HTMLElement, styleAttrs: Partial<CSSStyleDeclaration> = {}): HTMLVideoElement {
    if (this.video) return this.video;

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    `;
    Object.assign(video.style, styleAttrs);
    video.onerror = (e) => console.error('[MseVideoPlayer] video error', e);

    container.appendChild(video);
    this.video = video;

    this.mediaSource = new MediaSource();
    this.mediaSourceObjectUrl = URL.createObjectURL(this.mediaSource);
    video.src = this.mediaSourceObjectUrl;

    this.sourceReady = new Promise<void>((resolve) => {
      this.resolveSourceReady = resolve;
    });
    this.mediaSource.addEventListener('sourceopen', () => {
      if (!this.mediaSource) return;
      try {
        const sb = this.mediaSource.addSourceBuffer(MseVideoPlayer.FLEET_CODEC);
        sb.mode = 'segments';
        this.sourceBuffer = sb;
        this.resolveSourceReady?.();
      } catch (e) {
        console.error('[MseVideoPlayer] addSourceBuffer failed:', e);
      }
    });

    // Trim past buffered ranges as playback progresses so we stay under
    // the buffer quota. Cheap operation; safe to run every timeupdate
    // (~250 ms cadence).
    video.addEventListener('timeupdate', () => this.recycleBuffered(video.currentTime));

    return video;
  }

  /** Tears the player down and releases all DOM + MediaSource state. */
  detach(): void {
    if (this.video) {
      try {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
      } catch { /* ignore */ }
      try { this.video.remove(); } catch { /* ignore */ }
    }
    if (this.mediaSourceObjectUrl) {
      URL.revokeObjectURL(this.mediaSourceObjectUrl);
    }
    this.video = null;
    this.mediaSource = null;
    this.sourceBuffer = null;
    this.mediaSourceObjectUrl = null;
    this.sourceReady = null;
    this.resolveSourceReady = null;
    this.timeline = [];
    this.nextTimestampOffsetSec = 0;
    this.appendedItemIds.clear();
  }

  /** Reset the timeline (e.g. on schedule change). Keeps the video element. */
  async reset(): Promise<void> {
    if (!this.sourceBuffer || !this.video) return;
    await this.waitForSourceBufferIdle();
    try {
      this.sourceBuffer.remove(0, Number.MAX_SAFE_INTEGER);
      await this.waitForUpdateEnd();
    } catch { /* ignore */ }
    this.timeline = [];
    this.nextTimestampOffsetSec = 0;
    this.appendedItemIds.clear();
    try { this.video.currentTime = 0; } catch { /* ignore */ }
  }

  /**
   * Append one playlist item to the MediaSource. Returns true on success,
   * false if the asset isn't a fragmented MP4 (caller should fall back to
   * canvas-mask path for that one item).
   */
  async appendItem(asset: ManifestAsset): Promise<boolean> {
    if (asset.type !== 'video') return false;
    if (!this.sourceBuffer || !this.video || !this.sourceReady) return false;
    if (this.appendedItemIds.has(asset.id)) {
      // Already in the timeline — no need to re-append.
      return true;
    }

    await this.sourceReady;

    // Get the raw bytes. Prefer the IndexedDB-cached Blob over network.
    const bytes = await this.fetchAssetBytes(asset);
    if (!bytes) return false;

    // Sniff for fragmented-MP4 boxes before appending. SourceBuffer
    // refuses non-fragmented input silently or with cryptic errors.
    if (!isFragmentedMp4(bytes)) {
      console.warn(`[MseVideoPlayer] asset ${asset.id} is not fragmented MP4 — caller falls back`);
      return false;
    }

    await this.waitForSourceBufferIdle();
    try {
      this.sourceBuffer.timestampOffset = this.nextTimestampOffsetSec;
      this.sourceBuffer.appendBuffer(bytes);
      await this.waitForUpdateEnd();
    } catch (e) {
      console.error(`[MseVideoPlayer] appendBuffer failed for ${asset.id}:`, e);
      return false;
    }

    // Read back the actual duration from the buffered ranges (most
    // reliable; the manifest's reported duration can be slightly off).
    const durationSec = this.readBackLastSegmentDuration() ?? (asset.duration ?? 10);

    this.timeline.push({
      assetId: asset.id,
      startSec: this.nextTimestampOffsetSec,
      durationSec,
    });
    this.nextTimestampOffsetSec += durationSec;
    this.appendedItemIds.add(asset.id);

    console.log(
      `[MseVideoPlayer] appended id=${asset.id} startSec=${this.timeline[this.timeline.length - 1].startSec.toFixed(2)} dur=${durationSec.toFixed(2)}`,
    );

    return true;
  }

  /** Returns the most-recently appended item's start time in the MSE timeline. */
  getLatestItemStartSec(): number | null {
    const entry = this.timeline[this.timeline.length - 1];
    return entry ? entry.startSec : null;
  }

  /** The persistent <video> element so the renderer can manage its styling. */
  getVideoElement(): HTMLVideoElement | null {
    return this.video;
  }

  /** Has this asset already been appended? */
  hasItem(assetId: string): boolean {
    return this.appendedItemIds.has(assetId);
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private async fetchAssetBytes(asset: ManifestAsset): Promise<ArrayBuffer | null> {
    // 1. Try the IndexedDB cache directly — this is what manifestLoader
    //    populates during precacheAssets, so steady-state hits here.
    const cached = await getCachedAsset(asset.id).catch(() => undefined);
    if (cached) {
      return await cached.arrayBuffer();
    }
    // 2. Fallback: fetch the URL the manifestLoader would hand us.
    const url = (await manifestLoader.getAssetUrl(asset.id)) || asset.url;
    if (!url) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      return await res.arrayBuffer();
    } catch (e) {
      console.warn(`[MseVideoPlayer] fetch failed for ${asset.id}:`, e);
      return null;
    }
  }

  private async waitForSourceBufferIdle(): Promise<void> {
    if (!this.sourceBuffer) return;
    while (this.sourceBuffer.updating) {
      await this.waitForUpdateEnd();
    }
  }

  private waitForUpdateEnd(): Promise<void> {
    if (!this.sourceBuffer) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const sb = this.sourceBuffer!;
      const done = () => {
        sb.removeEventListener('updateend', done);
        sb.removeEventListener('error', done);
        resolve();
      };
      sb.addEventListener('updateend', done, { once: true });
      sb.addEventListener('error', done, { once: true });
    });
  }

  private readBackLastSegmentDuration(): number | null {
    if (!this.sourceBuffer || this.sourceBuffer.buffered.length === 0) return null;
    const ranges = this.sourceBuffer.buffered;
    const lastEnd = ranges.end(ranges.length - 1);
    return lastEnd - this.nextTimestampOffsetSec;
  }

  private async recycleBuffered(currentTime: number): Promise<void> {
    if (!this.sourceBuffer || this.sourceBuffer.buffered.length === 0) return;
    if (this.sourceBuffer.updating) return;
    const earliestKeep = currentTime - KEEP_PAST_SEC;
    if (earliestKeep <= 0) return;
    const ranges = this.sourceBuffer.buffered;
    const start = ranges.start(0);
    if (earliestKeep <= start + 0.1) return;
    try {
      this.sourceBuffer.remove(start, earliestKeep);
    } catch (e) {
      // QuotaExceeded or transient; ignore — next timeupdate retries.
      void e;
    }
  }
}

/**
 * Sniff the first 64 KB for fragmented-MP4 box signatures. A fragmented
 * MP4 has `styp` at offset 0 OR an early `moof` box. A non-fragmented
 * MP4 has `moov` near the top and no `moof` boxes anywhere.
 */
function isFragmentedMp4(bytes: ArrayBuffer): boolean {
  const view = new DataView(bytes, 0, Math.min(SNIFF_BYTES, bytes.byteLength));
  const text = new TextDecoder('latin1').decode(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
  // `styp` (segment type) at offset 4 of the first box is the canonical
  // fragmented-MP4 marker. ftyp+moof is also acceptable (moof can be
  // anywhere). moov+mdat-only (no moof) is non-fragmented.
  return text.includes('styp') || text.includes('moof');
}
